import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { KaboodleToaster } from "components/common";
import DownloadLink from "components/common/DownloadLink.tsx";
import { TextArea, TextInput } from "components/inputs";
import KaboodleModal from "components/Modals/KaboodleModal";
import { format } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { useLazyGetEmailTemplateQuery } from "services/email-api.ts";
import { useGetDocumentsQuery } from "services/documents-api.ts";
import { DATE_AND_TIME_FORMAT } from "utils/date-utils";
import { extractErrorMessage } from "utils/error-utils";

// Matches emails.constants.EMAIL_CATEGORY.RATED_AGE_REQUEST on the backend.
const RATED_AGE_REQUEST_EMAIL_TYPE = "rated_age_request";

// Deliberately permissive (just "something@something.something") since this
// only needs to catch obvious typos. Same pattern AckEmailModal uses.
const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;

// Some Outlook/Windows configs silently truncate mailto: links around
// ~2000 chars. This is a conservative threshold below that to warn staff
// before it happens, since a silently cut-off body is easy to miss.
const MAILTO_LENGTH_WARNING_THRESHOLD = 1800;

type RatedAgeRequestModalProps = {
  isOpen: boolean;
  onClose: () => void;
  fileId: string;
};

const copyToClipboard = (value: string, label: string) => {
  navigator.clipboard.writeText(value).then(
    () => KaboodleToaster.success(`${label} copied to clipboard.`),
    () => KaboodleToaster.error(`Failed to copy ${label}.`),
  );
};

const RatedAgeRequestModal = ({
  isOpen,
  onClose,
  fileId,
}: RatedAgeRequestModalProps) => {
  const [recipient, setRecipient] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  const [fetchTemplate, { isFetching: isTemplateLoading }] =
    useLazyGetEmailTemplateQuery();

  // No limit here: a file that's had many records for rated age uploaded
  // would render a long, unpaginated list. Low risk today given how rarely
  // these get re-uploaded per file, but worth adding a limit/pagination if
  // that changes.
  //
  // Sorted by document_date (defaults to the upload moment, but staff can
  // edit it like any other document's date), not created/modified, since
  // that's the same "when this document is dated" field every other
  // document list in the app already sorts and displays by.
  const { data: ratedAgeRecordsDocuments, isFetching: isDocumentsLoading } =
    useGetDocumentsQuery(
      {
        fileId,
        sortBy: "-document_date",
        filters: { type: "RECORDS_FOR_RATED_AGE" },
      },
      { skip: !isOpen },
    );

  // Mirrors AckEmailModal's open-transition pattern: the parent only mounts
  // this while isOpen is true, so the first render already has isOpen=true.
  // Starting this ref at false is what makes that first render register as
  // "just opened" below.
  const wasOpenRef = useRef(false);
  // Recipient isn't readOnly while the template loads (unlike subject/body
  // below), so the user can start typing before the fetch resolves. Guard
  // against the late response clobbering that, same as AckEmailModal's
  // hasEditedToEmailRef.
  const hasEditedRecipientRef = useRef(false);
  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;

    if (justOpened) {
      hasEditedRecipientRef.current = false;
      setRecipient("");
      setSubject("");
      setBody("");
      fetchTemplate({ emailType: RATED_AGE_REQUEST_EMAIL_TYPE, fileId })
        .unwrap()
        .then((template) => {
          if (!hasEditedRecipientRef.current) {
            setRecipient(template.recipient ?? "");
          }
          setSubject(template.subject);
          setBody(template.body);
        })
        .catch((error) => {
          console.error(error);
          KaboodleToaster.error(
            extractErrorMessage(
              error,
              "Failed to load the rated age request template.",
            ),
          );
        });
    }
  }, [isOpen, fileId, fetchTemplate]);

  const hasInvalidRecipient =
    recipient !== "" && !EMAIL_PATTERN.test(recipient);
  const canOpenInOutlook = recipient !== "" && !hasInvalidRecipient;
  // The \n keeps this from undercounting a subject/body pair that'd read
  // differently concatenated raw (e.g. "A" + "B" vs "AB"); doesn't need to
  // match mailto's real separators exactly since this is a conservative
  // early warning, not a precise limit.
  const isMailtoLikelyTruncated =
    encodeURIComponent(`${subject}\n${body}`).length >
    MAILTO_LENGTH_WARNING_THRESHOLD;

  const handleOpenInOutlook = () => {
    if (!canOpenInOutlook) {
      return;
    }
    // A mailto: link can't carry attachments (no browser/OS API allows a
    // page to force-attach a file to the OS mail client), so staff still
    // have to manually attach the relevant records for rated age from the
    // list below in Outlook.
    //
    // Note: mailto URIs have a practical length limit (~2000 chars in some
    // Outlook/Windows configs), which is why isMailtoLikelyTruncated warns
    // above before it silently happens.
    //
    // A temporary <a> click (not window.location.href) avoids navigating
    // the current tab to the mailto: URL, which some browsers treat as a
    // real navigation and show a "leave page" warning for. Safari and
    // Firefox ignore .click() on an element that was never attached to the
    // document, so it has to be appended before clicking and removed after.
    const link = document.createElement("a");
    link.href = `mailto:${recipient}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <KaboodleModal
      isOpen={isOpen}
      onClose={onClose}
      size="large"
      footer={
        <div className="flex jc-c">
          {/* A lone .button gets the full pill border-radius (30px) at its
          default 50px height, which reads as a circle for text this short.
          The extra width keeps it a clearly rounded rectangle instead. */}
          <Button onClick={onClose} style={{ minWidth: "120px" }}>
            Close
          </Button>
        </div>
      }
    >
      <Card heading="Rated Age Request">
        <div className="flex ai-c">
          <div style={{ flex: 1 }}>
            <TextInput
              name="ratedAgeRequestRecipient"
              label="Recipient Email"
              type="email"
              value={recipient}
              onChange={(e) => {
                hasEditedRecipientRef.current = true;
                setRecipient(e.target.value);
              }}
            />
          </div>
          {/* disabled while loading, same as subject/body's copy buttons
          below, so this can't copy the placeholder empty string before the
          template arrives. Recipient itself stays editable during the load
          (unlike subject/body) so the user can start typing early. */}
          <Button
            onClick={() => copyToClipboard(recipient, "Recipient")}
            disabled={isTemplateLoading}
          >
            <span className="material-icons">content_copy</span>
          </Button>
        </div>
        {hasInvalidRecipient && (
          <div className="error-message">Enter a valid recipient email.</div>
        )}
        <div className="flex ai-c">
          <div style={{ flex: 1 }}>
            <TextInput
              name="ratedAgeRequestSubject"
              label="Subject"
              value={isTemplateLoading ? "Loading..." : subject}
              readOnly={isTemplateLoading}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>
          <Button
            onClick={() => copyToClipboard(subject, "Subject")}
            disabled={isTemplateLoading}
          >
            <span className="material-icons">content_copy</span>
          </Button>
        </div>
        <div className="flex ai-c">
          <div style={{ flex: 1 }}>
            <TextArea
              name="ratedAgeRequestBody"
              label="Body"
              value={isTemplateLoading ? "Loading..." : body}
              readOnly={isTemplateLoading}
              // TextArea's onChange type really is ChangeEvent | string
              // (see TextArea.tsx), not a mismatch with Subject's handler
              // above, so this check is required, not defensive leftover.
              onChange={(e) =>
                setBody(typeof e === "string" ? e : e.target.value)
              }
              rows={16}
            />
          </div>
          <Button
            onClick={() => copyToClipboard(body, "Body")}
            disabled={isTemplateLoading}
          >
            <span className="material-icons">content_copy</span>
          </Button>
        </div>
        <Button onClick={handleOpenInOutlook} disabled={!canOpenInOutlook}>
          Open in Outlook
        </Button>
        {isMailtoLikelyTruncated && (
          <div className="error-message">
            This body is long enough that Outlook may cut it off. Use the Copy
            buttons above to be safe.
          </div>
        )}
      </Card>

      <Card heading="Records for Rated Age">
        {isDocumentsLoading ? (
          <p>Loading...</p>
        ) : ratedAgeRecordsDocuments?.results?.length ? (
          <ul>
            {ratedAgeRecordsDocuments.results.map((document) => (
              <li key={document.id} className="flex ai-c jc-sb">
                {/* title shows the full name on hover once a long filename
                gets cut off by the ellipsis below */}
                <span
                  title={document.original_filename}
                  style={{
                    flex: "1 1 0%",
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {document.original_filename}
                </span>
                <span style={{ flexShrink: 0 }}>
                  {format(
                    new Date(document.document_date),
                    DATE_AND_TIME_FORMAT,
                  )}
                </span>
                <DownloadLink
                  url={document.proxy_url}
                  filename={document.original_filename}
                  title="Download"
                  icon="download"
                />
              </li>
            ))}
          </ul>
        ) : (
          <p>No records for rated age have been uploaded yet.</p>
        )}
      </Card>
    </KaboodleModal>
  );
};

export default RatedAgeRequestModal;
